import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import type { ChannelContent } from './types';
import { ArrowGlyph, SectionHeading, tone } from '../primitives';

/**
 * The channel's limits and plan, stated plainly before anyone signs up, so
 * nobody finds out halfway through the setup.
 */
export default function ChannelRules({
  rules,
}: {
  rules: ChannelContent['rules'];
}) {
  return (
    <section id='good-to-know' className='py-20 sm:py-28'>
      <div className='mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-8'>
        <div className='lg:sticky lg:top-28 lg:col-span-4 lg:self-start'>
          <SectionHeading size='md' title={rules.title} tail={rules.tail} />
        </div>

        <dl className='grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:col-span-8'>
          {rules.items.map(({ icon: Icon, title, detail, link }) => (
            <div
              key={title}
              className='border-t border-[#0f0f28]/[0.08] pt-6 dark:border-white/[0.08]'
            >
              <dt
                className={clsxm(
                  'flex items-center gap-3 text-[16px] font-semibold',
                  tone.ink,
                )}
              >
                <span className='flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-400/10 dark:text-primary-300'>
                  <Icon className='h-[18px] w-[18px]' />
                </span>
                {title}
              </dt>
              <dd className={clsxm('mt-3 text-[15px] leading-7', tone.body)}>
                {detail}
                {link ? <RuleLink {...link} /> : null}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/**
 * Pages of this site keep the visitor's language through next/link; the
 * blog is English-only, so it gets a plain link rather than a /pt/blog
 * address that only redirects.
 */
function RuleLink({ href, label }: { href: string; label: string }) {
  const className =
    'group mt-3 block w-fit text-sm font-semibold text-primary-700 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200';
  const content = (
    <>
      {label}
      <ArrowGlyph className='ms-1.5 inline-block h-3.5 w-3.5 align-[-2px] transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
    </>
  );
  return href.startsWith('/blog') ? (
    <a href={href} className={className}>
      {content}
    </a>
  ) : (
    <Link href={href} className={className}>
      {content}
    </Link>
  );
}
